import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import brandConfigDevelopment from "../config/brand-development.json";
import brandConfigProduction from "../config/brand-production.json";
import { PSAuthConfig } from "@pineseed/workspace/handlers/auth-handler";
import { BrandConfig } from "@pineseed/workspace/config/brand";
import { GuiProvider } from "@pineseed/gui/provider";
import type { AcpAvatarChatConfig } from "@pineseed/interface-gui/component/AcpAvatarChat";

function requireEnv(name: string): string {
  const value = import.meta.env[name] as string | undefined;
  if (!value) throw new Error(`Missing required env var: ${name}`);
  return value;
}

function requireEnvInt(name: string): number {
  const raw = requireEnv(name);
  const n = Number(raw);
  if (!Number.isInteger(n)) throw new Error(`${name} must be a valid integer, got: ${raw}`);
  return n;
}

const isDevelopment = import.meta.env.MODE === "development";
const config = isDevelopment
  ? brandConfigDevelopment
  : (brandConfigProduction as unknown as BrandConfig);
const { auth } = config;
auth.casdoor.ui.mode = "popup" as const;

const useSSL = import.meta.env.VITE_EDGE_PROXY_USE_SSL !== "false";
const httpUrl = `${useSSL ? "https" : "http"}://${import.meta.env.VITE_EDGE_PROXY_HOST}:${useSSL ? import.meta.env.VITE_EDGE_PROXY_SSL_PORT : import.meta.env.VITE_EDGE_PROXY_PORT}`;
const wsUrl = `${useSSL ? "wss" : "ws"}://${import.meta.env.VITE_EDGE_PROXY_HOST}:${useSSL ? import.meta.env.VITE_EDGE_PROXY_SSL_PORT : import.meta.env.VITE_EDGE_PROXY_PORT}/ws`;

const accountId = requireEnv("VITE_ACCOUNT_ID");
const apiKeyTtlS = requireEnvInt("VITE_API_KEY_TTL_S");

const acpAvatarChatConfig: AcpAvatarChatConfig = {
  httpUrl,
  wsUrl,
  location: "admin-sandbox",
  loadSessionTimeoutMs: requireEnvInt("VITE_ACP_LOAD_SESSION_TIMEOUT_MS"),
  uploadTtlS: requireEnvInt("VITE_ACP_UPLOAD_TTL_S"),
  reactionEmojis: ["👍", "❤️", "😂", "😮", "🎉"],
  closeOnSendDelayMs: 250,
  avatarChat: {
    size: 192,
    facesBasePath: "/faces/",
    verticalRange: { min: 25, max: 75 },
    shockwave: {
      duration: 750,
      ringCount: 2,
      colors: [
        "border-4 border-black/60",
        "border-4 border-white/30",
      ],
      offset: "0",
      startScale: 0.95,
      endScale: 2,
    },
    emojiBurst: {
      duration: 1000,
      emojiCount: 16,
      startSize: 24,
      endSize: 96,
    },
  },
};

const container = document.getElementById("root");
if (!container) throw new Error("Root element not found");

const root = createRoot(container);
root.render(
  <StrictMode>
    <GuiProvider>
      <App
        pineSeedAuth={auth as unknown as PSAuthConfig}
        httpUrl={httpUrl}
        wsUrl={wsUrl}
        accountId={accountId}
        apiKeyTtlS={apiKeyTtlS}
        acpAvatarChatConfig={acpAvatarChatConfig}
      />
    </GuiProvider>
  </StrictMode>,
);

// Mark root as loaded to fade in
container.classList.add("loaded");
